"use client"
import AppTooltipWrapper from "@/app/components/atomic/molecules/AppTooltipWrapper"
import StarsRating from "@/app/components/atomic/StarsRating"
import ResultButton from "@/app/components/auctor/result/ResultButton"
import {IAppliedFilter, IColumnConfig, ITemplateConfig} from "@/app/models/auctor"
import {evaluateExpressionWithFiltersAndRowData, getClientQueryParams} from "@/app/utils/expression-utils"
import dynamic from "next/dynamic"
import Image from "next/image"
import React, {ReactNode, useState} from "react"
import "react-loading-skeleton/dist/skeleton.css"

const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"))

interface IResultColumnProps {
    column: IColumnConfig
    resultRow: any
    appliedFilters: IAppliedFilter[]
    isPreview?: boolean
    templateConfig: ITemplateConfig
    isWrappableOnMobile?: boolean
    setMobileWrappedColumn?: any
}

interface IResultColumnContentProps {
    column: IColumnConfig
    resultRow: any
    templateConfig: ITemplateConfig
}

interface IResultColumnWrapperProps {
    column: IColumnConfig
    isWrappableOnMobile: boolean | undefined
    children: ReactNode
}

export enum DATA_COLUMN_TYPES {
    STRING = "string",
    NUMBER = "number",
    HTML = "html",
    IMAGE_URL = "image_url",
    EXPRESSION = "expression",
    BUTTON = "button",
}

export default function ResultColumn({
                                         column,
                                         resultRow,
                                         appliedFilters,
                                         isPreview,
                                         templateConfig,
                                         isWrappableOnMobile,
                                         setMobileWrappedColumn,
                                     }: IResultColumnProps) {
    const [isWrappedOnMobile, setIsWrapped] = useState(isWrappableOnMobile)

    const queryParams = getClientQueryParams()

    const toggleWrappedOnMobile = () => {
        setIsWrapped(!isWrappedOnMobile)

        setMobileWrappedColumn(!isWrappedOnMobile ? null : column)
    }

    return (
        <>
            {isWrappableOnMobile && (
                <ResultColumnWrapButton
                    column={column}
                    isWrappedOnMobile={isWrappedOnMobile}
                    setIsWrapped={toggleWrappedOnMobile}
                />
            )}
            <ResultColumnWrapper column={column} isWrappableOnMobile={isWrappableOnMobile}>
                <ResultColumnContent column={column} resultRow={resultRow} templateConfig={templateConfig}/>
            </ResultColumnWrapper>
        </>
    )

    function ResultColumnWrapButton({
                                        column,
                                        isWrappedOnMobile,
                                        setIsWrapped,
                                    }: {
        column: IColumnConfig
        isWrappedOnMobile: boolean | undefined
        setIsWrapped: any
    }) {
        return (
            <div
                className="flex justify-center items-center w-full gap-2 cursor-pointer bg-white p-3 sm:hidden font-medium"
                onClick={() => setIsWrapped(!isWrappedOnMobile)}
            >
                {column.wrapped_label ?? "Afficher"}
                <span className={`fas fa-lg ${isWrappedOnMobile ? "fa-caret-down" : "fa-caret-up"}`}/>
            </div>
        )
    }

    function ResultColumnWrapper({column, isWrappableOnMobile, children}: IResultColumnWrapperProps) {
        const classNames = `sm:flex flex-col items-center justify-center px-4 py-2 overflow-hidden text-sm gap-3
        md:py-4 md:gap-4 md:col-span-${column.col_span} ${isWrappableOnMobile ? "hidden" : "flex"}`

        return (
            <>
                {column.tooltip && (
                    <AppTooltipWrapper id={column.id} text={column.tooltip} className={classNames}>
                        {children}
                    </AppTooltipWrapper>
                )}

                {!column.tooltip && (
                    <div key={column.id} className={classNames}>
                        {children}
                    </div>
                )}
            </>
        )
    }

    function getColSpanClass(colSpan: number): string {
        switch (colSpan) {
            case 1: return 'md:col-span-1';
            case 2: return 'md:col-span-2';
            case 3: return 'md:col-span-3';
            case 4: return 'md:col-span-4';
            case 5: return 'md:col-span-5';
            case 6: return 'md:col-span-6';
            case 7: return 'md:col-span-7';
            case 8: return 'md:col-span-8';
            case 9: return 'md:col-span-9';
            case 10: return 'md:col-span-10';
            case 11: return 'md:col-span-11';
            case 12: return 'md:col-span-12';
            default: return 'md:col-span-12';
        }
    }

    function ResultColumnContent({column, resultRow, templateConfig}: IResultColumnContentProps) {
        return (
            <>
                {column.top_additional_expression && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                evaluateExpressionWithFiltersAndRowData(
                                    column.top_additional_expression,
                                    resultRow,
                                    appliedFilters,
                                    undefined,
                                    queryParams
                                ) ?? "",
                        }}
                    />
                )}
                {column.column_type === DATA_COLUMN_TYPES.IMAGE_URL && column.data_column_name !== null && (
                    <>
                        <div className="flex justify-around w-full relative">
                            <ResultLogo
                                src={resultRow[column.data_column_name]}
                                alt={column.label ? column.label : "logo assureur"}
                            />

                            {templateConfig.rating_column_name && (
                                <div className="md:hidden flex items-center">
                                    <StarsRating
                                        onlyOneStar
                                        displayRatingNotation
                                        displayMaxNotation
                                        notation={parseFloat(resultRow[templateConfig.rating_column_name])}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
                {templateConfig.rating_column_name &&
                    column.show_rating &&
                    Number.isFinite(parseFloat(resultRow[templateConfig.rating_column_name])) && (
                        <div className="whitespace-nowrap justify-start items-center w-full gap-2 hidden md:flex">
                            <span className="text-base text-blue-700 font-semibold">Notre note</span>
                            <div className="bg-blue-100 rounded-full py-1 px-1.5 text-blue-700">
                                <StarsRating
                                    displayRatingNotation
                                    displayMaxNotation
                                    notationOnTheLeft
                                    noteFontSize="text-base"
                                    notation={parseFloat(resultRow[templateConfig.rating_column_name])}
                                />
                            </div>
                        </div>
                    )}
                {column.column_type === DATA_COLUMN_TYPES.HTML && column.data_column_name !== null && (
                    <div
                        className="self-start text-[13px] font-light leading-normal"
                        dangerouslySetInnerHTML={{
                            __html: resultRow[column.data_column_name] ?? "",
                        }}
                    />
                )}
                {column.column_type === DATA_COLUMN_TYPES.EXPRESSION && column.expression && (
                    <div
                        className="w-full"
                        dangerouslySetInnerHTML={{
                            __html:
                                evaluateExpressionWithFiltersAndRowData(
                                    column.expression,
                                    resultRow,
                                    appliedFilters,
                                    undefined,
                                    queryParams
                                ) ?? "",
                        }}
                    />
                )}
                {column.column_type === DATA_COLUMN_TYPES.STRING && column.data_column_name !== null && (
                    <div className="text-[13px] font-light leading-normal">{resultRow[column.data_column_name]}</div>
                )}
                {column.column_type === DATA_COLUMN_TYPES.NUMBER && column.data_column_name !== null && (
                    <div>{resultRow[column.data_column_name]}</div>
                )}
                {column.column_type === DATA_COLUMN_TYPES.BUTTON && templateConfig.price_expression !== null && (
                    <div
                        className="hidden md:flex"
                        dangerouslySetInnerHTML={{
                            __html:
                                evaluateExpressionWithFiltersAndRowData(
                                    templateConfig.price_expression,
                                    resultRow,
                                    appliedFilters,
                                    undefined,
                                    queryParams
                                ) ?? "",
                        }}
                    />
                )}
                {column.column_type === DATA_COLUMN_TYPES.BUTTON && (
                    <ResultButton
                        column={column}
                        resultRow={resultRow}
                        appliedFilters={appliedFilters}
                        queryParams={queryParams}
                    />
                )}
                {column.bottom_additional_expression && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                evaluateExpressionWithFiltersAndRowData(
                                    column.bottom_additional_expression,
                                    resultRow,
                                    appliedFilters,
                                    undefined,
                                    queryParams
                                ) ?? "",
                        }}
                    />
                )}
            </>
        )
    }
}

const ResultLogo = React.memo(
    ({src, alt}: { src: string; alt: string }) => {
        return (
            <div className="w-32 md:w-28 h-20 relative object-contain">
                <Image
                    src={src}
                    alt={alt}
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 40vw, (max-width: 1200px) 30vw, 10vw"
                />
            </div>
        )
    },
    (prevProps, nextProps) => prevProps.src === nextProps.src
)
ResultLogo.displayName = "ResultLogo"
